/**
 * Phase 1.7 — release banner. "New look. Same data."
 *
 * Shown at the top of every v2 page until the user dismisses it. The
 * dismissal is persisted in `ui_prefs.banners_dismissed[]` so it doesn't
 * re-appear on subsequent sessions. Once Phase 1.7 is fully rolled out
 * and the dismissal rate plateaus, the cleanup PR removes this component.
 */

import { Sparkles, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useUiPrefs } from '@/lib/layout-flag';
import { cn } from '@/lib/utils';

const BANNER_ID = 'phase-1.7-launch';

export function ReleaseBanner(): JSX.Element | null {
  const { prefs, patch } = useUiPrefs();
  const dismissed = prefs.banners_dismissed ?? [];
  if (dismissed.includes(BANNER_ID)) return null;

  const dismiss = (): void => {
    void patch({ banners_dismissed: [...dismissed, BANNER_ID] });
  };

  return (
    <div
      className={cn(
        'flex h-rail-h-banner items-center gap-3 border-b border-border bg-clay-opaque px-4',
        'dark:bg-clay-dark/20 dark:border-border',
      )}
      role="status"
    >
      <Sparkles className="h-4 w-4 text-clay dark:text-clay-dark" />
      <p className="flex-1 truncate text-sm">
        <span className="font-medium text-charcoal dark:text-charcoal-dark">New look. Same data.</span>
        <span className="ml-2 text-muted-foreground">
          We refreshed the chrome. Your records are unchanged.
        </span>
        <Link
          to="/dev/components"
          className="ml-3 hidden text-burnt-orange underline-offset-2 hover:underline dark:text-burnt-orange-dark sm:inline"
        >
          Tour the new components →
        </Link>
      </p>
      <button
        type="button"
        onClick={dismiss}
        aria-label="Dismiss"
        className="text-muted-foreground hover:text-charcoal dark:hover:text-charcoal-dark"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}
